import React, { useState } from 'react';
import { useAutoFix } from '../../context/AutoFixContext';
import {
  Activity,
  ShieldCheck,
  AlertTriangle,
  Wrench,
  CheckCircle2,
  Volume2,
  VolumeX,
  RotateCcw,
  Sparkles,
  Zap,
  Award,
  HelpCircle,
  Calculator,
} from 'lucide-react';
import { JudgeGuideModal } from './JudgeGuideModal';

export const Header: React.FC = () => {
  const { systemStatus, resetAll, soundEnabled, setSoundEnabled, registeredTools } =
    useAutoFix();
  const [isJudgeGuideOpen, setIsJudgeGuideOpen] = useState(false);

  const getStatusBadge = () => {
    switch (systemStatus) {
      case 'incident':
        return (
          <div
            className="badge badge-red"
            style={{
              padding: '4px 10px',
              fontSize: 11,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 0 12px rgba(239, 68, 68, 0.3)',
              animation: 'pulse 1.5s infinite',
            }}
          >
            <div className="status-dot status-incident" />
            <AlertTriangle size={12} />
            <span style={{ fontWeight: 700 }}>CRASH INTERCEPTED</span>
          </div>
        );
      case 'healing':
        return (
          <div
            className="badge badge-amber"
            style={{
              padding: '4px 10px',
              fontSize: 11,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 0 12px rgba(245, 158, 11, 0.3)',
            }}
          >
            <div className="status-dot status-healing" />
            <Wrench size={12} className="animate-spin" />
            <span style={{ fontWeight: 700 }}>WEBMCP REPAIRING...</span>
          </div>
        );
      case 'resolved':
        return (
          <div
            className="badge badge-green"
            style={{
              padding: '4px 10px',
              fontSize: 11,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              boxShadow: '0 0 14px rgba(16, 185, 129, 0.3)',
            }}
          >
            <div className="status-dot status-resolved" />
            <CheckCircle2 size={12} />
            <span style={{ fontWeight: 700 }}>AUTONOMOUSLY HEALED</span>
          </div>
        );
      case 'monitoring':
      default:
        return (
          <div
            className="badge badge-green"
            style={{ padding: '4px 10px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <div className="status-dot status-monitoring" />
            <ShieldCheck size={12} />
            <span>WEBMCP GUARD ACTIVE</span>
          </div>
        );
    }
  };

  return (
    <header className="header" style={{ height: '52px', borderBottom: '1px solid rgba(148, 163, 184, 0.15)' }}>
      {/* Brand & Identity */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              boxShadow: '0 0 16px rgba(16,185,129,0.5)',
            }}
          >
            <Activity size={18} />
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 16, fontWeight: 800, letterSpacing: '-0.3px', color: '#fff' }}>
                AutoFix<span style={{ color: 'var(--accent)' }}>.run</span>
              </span>
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  background: 'rgba(99, 102, 241, 0.18)',
                  border: '1px solid #6366f1',
                  color: '#a5b4fc',
                  padding: '2px 6px',
                  borderRadius: 4,
                  fontFamily: 'var(--font-mono)',
                  letterSpacing: '0.5px',
                }}
              >
                WebMCP v1.0
              </span>
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  background: 'rgba(139, 92, 246, 0.18)',
                  border: '1px solid #8b5cf6',
                  color: '#c4b5fd',
                  padding: '2px 6px',
                  borderRadius: 4,
                  fontFamily: 'var(--font-mono)',
                }}
              >
                Gemini 3.8 Flash
              </span>
            </div>
            <div style={{ fontSize: 10, color: 'var(--text-3)', lineHeight: 1.2 }}>
              OpenAI &amp; Netlify WebMCP Challenge Submission
            </div>
          </div>
        </div>

        {/* Live Status Indicator */}
        <div style={{ marginLeft: 12 }}>{getStatusBadge()}</div>
      </div>

      {/* Right Controls */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {/* Tool Count Badge */}
        <div
          style={{
            fontSize: 11,
            color: 'var(--text-2)',
            background: 'var(--bg-card)',
            padding: '5px 10px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
          title="Tools registered on window.navigator.modelContext"
        >
          <Sparkles size={13} style={{ color: 'var(--accent)' }} />
          <span>
            <strong style={{ color: '#fff' }}>{registeredTools.length}</strong> WebMCP Tools
          </span>
        </div>

        {/* Judge & Evaluator Guide Button */}
        <button
          className="btn btn-sm"
          onClick={() => setIsJudgeGuideOpen(true)}
          style={{
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.25) 0%, rgba(79, 70, 229, 0.25) 100%)',
            border: '1px solid #6366f1',
            color: '#c7d2fe',
            fontWeight: 700,
            fontSize: 11,
            padding: '5px 12px',
            borderRadius: 'var(--radius-sm)',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            boxShadow: '0 0 10px rgba(99, 102, 241, 0.2)',
          }}
          title="Open Hackathon Judge & Evaluator Walkthrough Guide"
        >
          <Award size={13} style={{ color: '#a5b4fc' }} />
          <span>Judge Guide</span>
        </button>

        {/* Quick Switch to Real-World Calculator */}
        <button
          className="btn btn-sm"
          onClick={() => {
            window.dispatchEvent(new CustomEvent('autofix-select-target', { detail: 'calculator' }));
          }}
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: '#38bdf8',
            background: 'rgba(56, 189, 248, 0.1)',
            border: '1px solid rgba(56, 189, 248, 0.3)',
            padding: '5px 10px',
            borderRadius: 'var(--radius-sm)',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
          title="Switch Target App pane to the open-source React Calculator"
        >
          <Calculator size={12} />
          <span>Calculator Trial</span>
        </button>

        {/* Sound Toggle */}
        <button
          className="btn btn-sm"
          onClick={() => setSoundEnabled(!soundEnabled)}
          title={soundEnabled ? 'Mute audio' : 'Enable audio'}
          style={{ padding: '6px 8px' }}
        >
          {soundEnabled ? (
            <Volume2 size={13} style={{ color: 'var(--accent)' }} />
          ) : (
            <VolumeX size={13} style={{ color: 'var(--text-3)' }} />
          )}
        </button>

        {/* Reset State */}
        <button
          className="btn btn-sm"
          onClick={resetAll}
          title="Reset target app state and clear incidents"
          style={{ padding: '6px 12px', background: 'var(--bg-card)' }}
        >
          <RotateCcw size={12} /> Reset App
        </button>
      </div>

      {/* Judge & Evaluator Guide Modal */}
      <JudgeGuideModal isOpen={isJudgeGuideOpen} onClose={() => setIsJudgeGuideOpen(false)} />
    </header>
  );
};
